import {useMainEquipments, useRequiredConfig} from '@domain/shifthandover'
import {ContentBox} from '@hconnect/common/components/ContentBox'
import {useExpandedGroups} from '@hconnect/common/hooks/useExpandGroups'
import {EquipmentData, Priority, ShiftEvent, ShiftEventCategory} from '@hconnect/common/types'
import {ExpandCollapseButton} from '@hconnect/uikit/src/lib2'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {Add as AddIcon} from '@mui/icons-material'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import {Button, Paper, Box, Typography, MenuItem, Menu, Switch} from '@mui/material'
import {
  ShiftEventDetailsModes,
  getItemId,
  ShiftEventDetailsWidget as SideCard
} from '@widgets/shift-event-details'
import {FilterOptions} from '@widgets/shift-event-filters'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {
  SHIFT_SUMMARY_EVENTS_PAGE_SIZE,
  EventListGroup,
  FEATURE_NAMESPACE,
  generalPinnedGroups,
  maintenancePinnedGroups,
  SHIFT_SUMMARY_TITLE_ID
} from '../consts'
import {usePreviousValue, useScrollToElement, useShiftSummaryEvents} from '../hooks'

import {GroupEventsSummary} from './GroupEventsSummary'

type Props = {
  plantId: string
  filterOptions: FilterOptions
}

type SettingsMenuProps = {
  priority: Priority[] | undefined
  setPriority: (priority?: Priority[]) => void
}

const SettingsMenu: React.FC<SettingsMenuProps> = ({priority, setPriority}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [anchorEl, setAnchorEl] = useState<null | Element>(null)
  const handleOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget as Element)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <>
      <Button
        variant="text"
        data-test-id={'summary-event-settings'}
        onClick={handleOpen}
        startIcon={<SettingsOutlinedIcon color="primary" />}
        sx={{
          '& .MuiButton-startIcon': {
            marginRight: 0
          }
        }}
      />

      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
        <MenuItem>
          <Box component={'span'} fontWeight={400} fontSize={16} whiteSpace="nowrap">
            {t('summary.action.prioritySwitch')}{' '}
            <Switch
              checked={!!priority}
              data-test-id="summary-priorities-filter-toggle"
              onChange={() => setPriority(priority ? undefined : [1, 2])}
              color="primary"
            />
          </Box>
        </MenuItem>
      </Menu>
    </>
  )
}

const getDefaultValues = (
  categoryId: EventListGroup,
  groupId: string,
  mainEquipments?: EquipmentData[]
): Partial<ShiftEvent> => {
  switch (categoryId) {
    case EventListGroup.Production:
      if (!mainEquipments) throw new Error('Main equipments are required for production events')
      return {
        category: ShiftEventCategory.Production,
        eventType: 'information',
        mainEquipment: mainEquipments.find((equipment) => equipment.id === groupId)
      }
    case EventListGroup.General:
      switch (groupId) {
        case 'healthAndSafety':
          return {
            category: ShiftEventCategory.HealthAndSafety,
            eventType: 'healthAndSafety'
          }
        case 'environment':
          return {
            category: ShiftEventCategory.Environment,
            eventType: 'environment'
          }
        default:
          return {
            category: ShiftEventCategory.Quality,
            eventType: 'quality'
          }
      }
    default:
      if (groupId === 'mechanical') {
        return {
          category: ShiftEventCategory.Mechanical,
          eventType: 'information'
        }
      }
      return {
        category: ShiftEventCategory.Electrical,
        eventType: 'information'
      }
  }
}

export const ShiftSummaryEvents: React.FC<Props> = ({
  plantId,
  filterOptions: filterOptionsProps
}) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const language = i18n.language
  const {shifts} = useRequiredConfig(plantId)

  const [priority, setPriority] = useState<Priority[] | undefined>()

  const filterOptions: FilterOptions = useMemo(
    () => ({...filterOptionsProps, priority: priority || filterOptionsProps.priority}),
    [filterOptionsProps, priority]
  )

  const [generalGroup, productionGroup, maintenanceGroup, areEventsLoading, totalItemCount] =
    useShiftSummaryEvents({plantId, shifts, options: filterOptions})

  const groupNames = Array.from([
    ...generalGroup.keys(),
    ...productionGroup.keys(),
    ...maintenanceGroup.keys()
  ])

  const [openGroups, toggleGroup] = useExpandedGroups(groupNames, 'summary-open-groups')
  const [sideCardMode, setSideCardMode] = useState<ShiftEventDetailsModes | null>(null)
  const previousActiveItem = usePreviousValue(sideCardMode ? getItemId(sideCardMode) : null)

  const sideCardRef = useRef<HTMLDivElement | null>(null)

  const scrollToSideCard = useScrollToElement(sideCardRef, SHIFT_SUMMARY_TITLE_ID)
  useEffect(scrollToSideCard, [scrollToSideCard, sideCardMode])

  const onItemClick = (item: ShiftEvent) => setSideCardMode({mode: 'detailsMode', itemId: item.id})
  const showSummaryPageMainEquipmentEvents = useCommonFeatureFlag(
    FeatureFlagKey.MainEquipmentList,
    {
      plantId
    }
  )
  const mainEquipmentsQuery = useMainEquipments({
    plantId,
    language,
    options: {
      enabled: showSummaryPageMainEquipmentEvents
    }
  })
  const onCreateClick = (categoryId: EventListGroup, groupId: string) => {
    setSideCardMode({
      mode: 'createMode',
      initialData: getDefaultValues(categoryId, groupId, mainEquipmentsQuery.data)
    })
  }

  const isAllProcessStagesSummaryEnabled = useCommonFeatureFlag(
    FeatureFlagKey.AllProcessStagesSummary,
    {
      plantId
    }
  )

  if (sideCardMode) {
    return (
      <Paper elevation={4} style={{overflow: 'hidden'}} ref={sideCardRef}>
        <SideCard
          {...sideCardMode}
          setMode={setSideCardMode}
          doClose={() => setSideCardMode(null)}
          data-test-id={'shift-summary-event-side-card'}
          initialData={sideCardMode.mode === 'createMode' ? sideCardMode.initialData : undefined}
          plantId={plantId}
        />
      </Paper>
    )
  }

  const eventGroupsCommonProps = {
    plantId,
    openGroups,
    toggleGroup,
    previousActiveItem,
    onItemClick,
    onCreateClick: showSummaryPageMainEquipmentEvents ? onCreateClick : undefined
  }

  return (
    <ContentBox
      title={
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          {t('summary.label.events')}
          <Box display="flex" alignItems="center" flexWrap="wrap">
            <ExpandCollapseButton
              expanded={openGroups && openGroups.length === groupNames.length}
              expandLabel={t('action.expand')}
              collapseLabel={t('action.collapse')}
              onClick={() => toggleGroup('all')}
              variant="text"
              data-test-id={'summary-events-all-toggle'}
            />
            <Button
              variant="text"
              data-test-id={'summary-event-event-create'}
              onClick={() => {
                setSideCardMode({mode: 'createMode'})
              }}
              sx={{
                '& .MuiButton-startIcon': {
                  marginRight: 0
                }
              }}
              startIcon={<AddIcon sx={{mr: 'unset'}} />}
            />
            <SettingsMenu priority={priority} setPriority={setPriority} />
          </Box>
        </Box>
      }
      bodyWithPadding
      mode={'contentDependentHeight'}
      isLoading={areEventsLoading}
      minHeight="450px"
    >
      <Typography>
        {totalItemCount > SHIFT_SUMMARY_EVENTS_PAGE_SIZE
          ? t('summary.label.totalItemCountHint', {
              totalItemCount,
              actualCount: SHIFT_SUMMARY_EVENTS_PAGE_SIZE
            })
          : undefined}
      </Typography>

      <GroupEventsSummary
        id={EventListGroup.General}
        title={t('summary.label.general')}
        events={{
          groupMap: generalGroup,
          pinnedGroups: generalPinnedGroups
        }}
        {...eventGroupsCommonProps}
      />
      <GroupEventsSummary
        id={EventListGroup.Production}
        title={t('summary.label.production')}
        events={{
          groupMap: productionGroup
        }}
        showCompleteList={showSummaryPageMainEquipmentEvents || isAllProcessStagesSummaryEnabled}
        {...eventGroupsCommonProps}
      />
      <GroupEventsSummary
        id={EventListGroup.Maintenance}
        title={t('summary.label.maintenance')}
        events={{
          groupMap: maintenanceGroup,
          pinnedGroups: maintenancePinnedGroups
        }}
        isLast
        {...eventGroupsCommonProps}
      />
    </ContentBox>
  )
}
